import { useMutationState } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo } from "react";

import type { ActiveModal } from "../layout/shipment-action-modals.tsx";
import { shipmentLockAtom } from "../state/shipment-lock-atoms.ts";
import type { CancelDispatchResponse } from "./use-post-cancel-dispatch.ts";

// Clears the "was force unlocked" banner state 20 seconds after a force unlock.
const useClearForceUnlockNotice = (wasForceUnlocked: boolean) => {
	const setLockState = useSetAtom(shipmentLockAtom);

	useEffect(() => {
		if (!wasForceUnlocked) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setLockState((prev) => ({ ...prev, wasForceUnlocked: false }));
		}, 20_000);

		return () => clearTimeout(timeoutId);
	}, [wasForceUnlocked, setLockState]);
};

// Opens the appropriate cancel-dispatch result modal after the mutation
// resolves, including API responses that report an unsuccessful cancellation.
const useCancelDispatchResultModal = (
	poNumber: number,
	setActiveModal: Dispatch<SetStateAction<ActiveModal>>,
) => {
	// Settled mutations outlive this page in the shared cache; ignore ones from earlier shipments.
	const mountedAt = useMemo(() => Date.now(), []);
	const cancelDispatchState = useMutationState({
		filters: {
			mutationKey: ["cancelDispatch"],
			predicate: (mutation) =>
				mutation.state.submittedAt >= mountedAt &&
				mutation.state.variables === poNumber,
		},
		select: (mutation) => ({
			status: mutation.state.status,
			data: mutation.state.data as CancelDispatchResponse | undefined,
		}),
	}).at(-1);

	useEffect(() => {
		if (cancelDispatchState?.status === "success") {
			if (
				cancelDispatchState.data?.success === false ||
				cancelDispatchState.data?.carrierDispatchCancelled === false
			) {
				setActiveModal({ type: "cancelDispatchError" });
				return;
			}
			setActiveModal({ type: "cancelDispatch" });
		} else if (cancelDispatchState?.status === "error") {
			setActiveModal({ type: "cancelDispatchError" });
		}
	}, [
		cancelDispatchState?.status,
		cancelDispatchState?.data?.success,
		cancelDispatchState?.data?.carrierDispatchCancelled,
		setActiveModal,
	]);
};

// Side effects for the shipment management layout: clearing the force-unlock
// banner and surfacing the cancel-dispatch result modal.
export const useShipmentManagementEffects = ({
	poNumber,
	wasForceUnlocked,
	setActiveModal,
}: {
	poNumber: number;
	wasForceUnlocked: boolean;
	setActiveModal: Dispatch<SetStateAction<ActiveModal>>;
}) => {
	useClearForceUnlockNotice(wasForceUnlocked);
	useCancelDispatchResultModal(poNumber, setActiveModal);
};
